import React, { useEffect, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useOAuthBridgeHandoff } from '../../apiHooks/mutations/oauthMutations';

/**
 * /oauth-connect mediates the OAuth2 authorize handoff: it accepts a
 * `?handoff=<state>` from the Go core API, posts the user's dashboard JWT to
 * /oauth-bridge/handoff, and navigates the browser to the returned
 * continue_url so the OAuth flow resumes.
 *
 * If the user isn't logged in yet, we stash the current URL in localStorage
 * and redirect to /auth. After login the user can navigate back manually (the
 * stashed URL is for a future enhancement of the auth page).
 */
const OAuthConnect = () => {
  const { isLoggedIn } = useAuthStore();
  const { search } = useLocation();
  const handoff = new URLSearchParams(search).get('handoff');

  const { mutateAsync: bridgeHandoff } = useOAuthBridgeHandoff();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!handoff) {
      setError('Missing handoff parameter.');
      return;
    }
    if (!isLoggedIn) {
      localStorage.setItem('oauth_return_url', window.location.pathname + window.location.search);
      return;
    }
    bridgeHandoff({ handoff })
      .then(({ data }) => {
        if (data?.continue_url) {
          window.location.assign(data.continue_url);
        } else {
          setError('Bridge response missing continue_url.');
        }
      })
      .catch(() => {
        setError('Failed to bind your identity to the authorize request. Please try again.');
      });
  }, [handoff, isLoggedIn, bridgeHandoff]);

  if (!isLoggedIn && handoff) {
    return <Navigate to="/auth" replace />;
  }

  return (
    <div className="flex h-screen items-center justify-center bg-gray-5">
      <div className="rounded-default border border-dark bg-white px-10 py-8 text-center">
        {error ? (
          <>
            <h1 className="mb-2 text-lg font-semibold text-red">Connection failed</h1>
            <p className="text-sm text-gray-2">{error}</p>
          </>
        ) : (
          <>
            <h1 className="mb-2 text-lg font-semibold text-primary">Connecting to Digger…</h1>
            <p className="text-sm text-gray-2">Finishing the authorization handoff. You will be redirected shortly.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default OAuthConnect;
